import { ItemCard } from "@/components/ItemCard";
import { InstallationQuotationCardDetails } from "@/features/orders/installationQuotation/InstallationQuotationCardDetails";
import { currencyFormatter } from "@/utils/currencyFormatter";
import { type InstallationQuotationData } from "@repo/api";
import { linkOutIcon } from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import { useSettings } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";

interface InstallationQuotationItemProps {
  editable?: boolean;
  installationQuotation: InstallationQuotationData;
}

export const InstallationQuotationItem = ({
  editable,
  installationQuotation,
}: InstallationQuotationItemProps) => {
  const { market, language, general } = useSettings();
  const { id, grandTotal, opportunityExternalId, quoteExternalId } =
    installationQuotation;

  const opportunityLink = `${import.meta.env.VITE__SALESFORCE_BASEURL}/${opportunityExternalId}`;
  const quoteLink = `${import.meta.env.VITE__SALESFORCE_BASEURL}/${quoteExternalId}`;

  const link = quoteExternalId ? quoteLink : opportunityLink;
  const openInSalesforce = {
    onOpenLink: () => {
      trackAnalytics("open_installation_quotation_in_salesforce");
    },
    disabled: !(
      Boolean(import.meta.env.VITE__SALESFORCE_BASEURL) &&
      (Boolean(opportunityExternalId) || Boolean(quoteExternalId))
    ),
  };

  return (
    <ItemCard
      editable={editable}
      hideImage={true}
      amount={currencyFormatter({
        value: grandTotal ?? 0,
        currency: general?.currency.currency_code,
        locale: getLanguageLocal(language, market),
        showSign: true,
        disableDecimals: Boolean(general?.localization?.disable_decimals),
        fractions: 2,
      })}
      actions={[
        {
          id: `open-link-button__installation-quote-${id}`,
          text: t("action.open_in_salesforce"),
          title: t("action.open_in_salesforce"),
          icon: linkOutIcon,
          iconPosition: "trailing",
          disabled: openInSalesforce.disabled,
          loading: false,
          href: openInSalesforce.disabled ? undefined : link,
          newWindow: true,
          onClick: openInSalesforce.onOpenLink,
        },
      ]}
      details={
        <InstallationQuotationCardDetails
          installationQuotation={installationQuotation}
        />
      }
    />
  );
};
